(() => {
  'use strict';

  const money = (cents, currency) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: currency || 'JPY',
        maximumFractionDigits: (currency || 'JPY') === 'JPY' ? 0 : 2
      }).format(amount);
    } catch (error) {
      return `¥${Math.round(amount).toLocaleString('ja-JP')}`;
    }
  };

  const hexToRgb = (value) => {
    const hex = String(value || '').trim().replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16)
    };
  };

  const fallbackToneFromAccent = (root) => {
    const accent = getComputedStyle(root).getPropertyValue('--jmgo-accent').trim();
    return hexToRgb(accent) || { r: 115, g: 25, b: 43 };
  };

  const getAverageImageTone = (img) => {
    const canvas = document.createElement('canvas');
    const size = 36;
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('Canvas unavailable');

    context.drawImage(img, 0, 0, size, size);
    const pixels = context.getImageData(0, 0, size, size).data;
    let r = 0;
    let g = 0;
    let b = 0;
    let totalWeight = 0;

    for (let i = 0; i < pixels.length; i += 4) {
      const alpha = pixels[i + 3] / 255;
      if (alpha < 0.1) continue;
      const pr = pixels[i];
      const pg = pixels[i + 1];
      const pb = pixels[i + 2];
      const max = Math.max(pr, pg, pb);
      const min = Math.min(pr, pg, pb);
      const chroma = (max - min) / 255;
      const luminance = (0.2126 * pr + 0.7152 * pg + 0.0722 * pb) / 255;
      const weight = alpha * (0.65 + chroma * 1.35) * (luminance > 0.96 ? 0.45 : 1);
      r += pr * weight;
      g += pg * weight;
      b += pb * weight;
      totalWeight += weight;
    }

    if (!totalWeight) throw new Error('No image pixels');
    return {
      r: Math.round(r / totalWeight),
      g: Math.round(g / totalWeight),
      b: Math.round(b / totalWeight)
    };
  };

  const applyTone = (root, tone) => {
    const strength = Math.max(0, Math.min(60, Number(root.dataset.jmgoToneStrength || 28)));
    const deep = {
      r: Math.max(0, Math.round(tone.r * 0.45)),
      g: Math.max(0, Math.round(tone.g * 0.45)),
      b: Math.max(0, Math.round(tone.b * 0.45))
    };
    const max = Math.max(tone.r, tone.g, tone.b);
    const min = Math.min(tone.r, tone.g, tone.b);
    const chroma = (max - min) / 255;
    const luminance = (0.2126 * tone.r + 0.7152 * tone.g + 0.0722 * tone.b) / 255;
    const wash = Math.min(0.24, (strength / 100) * 0.36);
    const ambient = Math.min(1, 0.58 + strength / 100 * 0.70);
    const saturate = 1.04 + Math.min(0.16, chroma * 0.11 + strength / 100 * 0.07 + (luminance < 0.48 ? 0.035 : 0));
    const contrast = (luminance < 0.38 ? 1.075 : 1.04) + strength / 100 * 0.035;

    const body = document.body;
    body.classList.add('jmgo-product-auto-tone');
    body.style.setProperty('--jmgo-auto-rgb', `${tone.r}, ${tone.g}, ${tone.b}`);
    body.style.setProperty('--jmgo-auto-deep-rgb', `${deep.r}, ${deep.g}, ${deep.b}`);
    body.style.setProperty('--jmgo-auto-wash', wash.toFixed(3));
    body.style.setProperty('--jmgo-hero-ambient-opacity', ambient.toFixed(2));
    body.style.setProperty('--jmgo-auto-saturate', saturate.toFixed(3));
    body.style.setProperty('--jmgo-auto-contrast', contrast.toFixed(3));
  };

  const initAutoTone = (root) => {
    if (!root || root.dataset.jmgoAutoTone !== 'true' || root.dataset.jmgoToneInitialized === 'true') return;
    root.dataset.jmgoToneInitialized = 'true';
    const image = root.querySelector('.jmgo-hero__tone-source');
    const fallback = fallbackToneFromAccent(root);

    if (!image) {
      applyTone(root, fallback);
      return;
    }

    const sample = () => {
      try {
        applyTone(root, getAverageImageTone(image));
      } catch (error) {
        applyTone(root, fallback);
      }
    };

    if (image.complete && image.naturalWidth) sample();
    else {
      image.addEventListener('load', sample, { once: true });
      image.addEventListener('error', () => applyTone(root, fallback), { once: true });
    }
  };

  const initHero = (root) => {
    if (!root) return;
    initAutoTone(root);
    if (root.dataset.jmgoHeroInitialized === 'true') return;
    root.dataset.jmgoHeroInitialized = 'true';

    const jsonNode = root.querySelector('[data-jmgo-variants-json]');
    if (!jsonNode) return;

    let data;
    try {
      data = JSON.parse(jsonNode.textContent);
    } catch (error) {
      return;
    }

    const optionIndex = Number(data.warrantyOptionIndex);
    const variantInputs = Array.from(root.querySelectorAll('[data-jmgo-variant-id]'));
    const priceNodes = Array.from(root.querySelectorAll('[data-jmgo-price]'));
    const compareNodes = Array.from(root.querySelectorAll('[data-jmgo-compare-price]'));
    const productSaleBadge = root.querySelector('[data-jmgo-product-sale-badge]');
    const productSaleDiscount = root.querySelector('[data-jmgo-product-sale-discount]');
    const submitButtons = Array.from(root.querySelectorAll('[data-jmgo-submit]'));
    const radios = Array.from(root.querySelectorAll('[data-jmgo-warranty]'));
    const stickyVariantLabels = Array.from(root.querySelectorAll('[data-jmgo-sticky-variant-label]'));
    const heroForm = root.querySelector('form.jmgo-product-form');
    const heroSubmit = heroForm ? heroForm.querySelector('button[type="submit"]') : null;
    const stickyCart = root.querySelector('[data-jmgo-sticky-cart]');
    const stickySubmit = root.querySelector('[data-jmgo-sticky-submit]');
    const heroActions = root.querySelector('.jmgo-hero__actions');

    let currentVariant = data.variants.find((variant) => String(variant.id) === String(data.currentVariantId)) || data.variants[0];

    const syncVariantUI = (variant) => {
      if (!variant) return;
      variantInputs.forEach((input) => { input.value = variant.id; });
      priceNodes.forEach((node) => { node.textContent = money(variant.price, data.currency); });
      const currentPrice = Number(variant.price || 0);
      const comparePrice = Number(variant.compare_at_price || 0);
      const showCompare = comparePrice > currentPrice;
      compareNodes.forEach((node) => {
        node.hidden = !showCompare;
        node.textContent = showCompare ? money(comparePrice, data.currency) : '';
      });

      if (productSaleBadge) {
        const mode = productSaleBadge.dataset.jmgoSaleBadgeMode || 'auto';
        const shouldShowBadge = mode === 'always' || (mode === 'auto' && showCompare);
        productSaleBadge.hidden = !shouldShowBadge || mode === 'hide';

        if (productSaleDiscount) {
          const showDiscount = productSaleBadge.dataset.jmgoSaleShowDiscount === 'true';
          let discountPercent = 0;
          if (showCompare && comparePrice > 0) {
            discountPercent = Math.max(0, Math.round(((comparePrice - currentPrice) / comparePrice) * 100));
          }
          productSaleDiscount.hidden = !showDiscount || discountPercent <= 0;
          productSaleDiscount.textContent = discountPercent > 0 ? `${discountPercent}% OFF` : '';
        }
      }

      submitButtons.forEach((button) => {
        button.disabled = !variant.available;
        const label = button.querySelector('[data-jmgo-submit-label]');
        if (label) {
          const availableLabel = button.dataset.jmgoAvailableLabel || data.addToCartLabel;
          label.textContent = variant.available ? availableLabel : data.soldOutLabel;
        }
      });

      if (stickyVariantLabels.length) {
        let labelText = '';
        if (Number.isInteger(optionIndex) && optionIndex >= 0 && variant.options && variant.options[optionIndex]) {
          labelText = variant.options[optionIndex];
        } else if (variant.options && variant.options.length && !String(variant.options[0] || '').match(/^Default/i)) {
          labelText = variant.options.join(' / ');
        }
        stickyVariantLabels.forEach((node) => {
          node.textContent = labelText;
          node.hidden = !labelText;
        });
      }
    };

    const updateVariant = (selectedValue) => {
      if (!currentVariant || !Number.isInteger(optionIndex) || optionIndex < 0) return;
      const desiredOptions = Array.from(currentVariant.options || []);
      desiredOptions[optionIndex] = selectedValue;

      const nextVariant = data.variants.find((variant) => {
        if (!variant.options || variant.options.length !== desiredOptions.length) return false;
        return variant.options.every((option, index) => String(option) === String(desiredOptions[index]));
      });
      if (!nextVariant) return;

      currentVariant = nextVariant;
      syncVariantUI(nextVariant);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', nextVariant.id);
      window.history.replaceState({}, '', url.toString());
    };

    if (Number.isInteger(optionIndex) && optionIndex >= 0 && radios.length) {
      radios.forEach((radio) => {
        radio.addEventListener('change', () => {
          if (radio.checked) updateVariant(radio.value);
        });
      });
    }

    if (stickySubmit && heroForm && heroSubmit) {
      stickySubmit.addEventListener('click', () => {
        if (stickySubmit.disabled || heroSubmit.disabled) return;
        if (typeof heroForm.requestSubmit === 'function') heroForm.requestSubmit(heroSubmit);
        else heroSubmit.click();
      });
    }

    if (stickyCart && heroActions) {
      const setStickyVisible = (visible) => {
        stickyCart.hidden = !visible;
        stickyCart.setAttribute('aria-hidden', visible ? 'false' : 'true');
        stickyCart.classList.toggle('is-visible', visible);
        document.body.classList.toggle('jmgo-sticky-cart-visible', visible);
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const pastHeroActions = root.getBoundingClientRect().top < 0;
            setStickyVisible(!entry.isIntersecting && pastHeroActions);
          });
        }, { threshold: 0.01 });
        observer.observe(heroActions);
      } else {
        let ticking = false;
        const updateSticky = () => {
          ticking = false;
          if (!document.documentElement.contains(root)) return;
          const rect = heroActions.getBoundingClientRect();
          setStickyVisible(rect.bottom < 0 && root.getBoundingClientRect().top < 0);
        };
        window.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(updateSticky);
        }, { passive: true });
        window.addEventListener('resize', updateSticky, { passive: true });
        updateSticky();
      }
    }

    syncVariantUI(currentVariant);
  };

  const setAccordionState = (item, open) => {
    if (!item) return;
    item.classList.toggle('is-open', open);
    const trigger = item.querySelector('[data-jmgo-accordion-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  const initAccordionGroup = (group) => {
    if (!group || group.dataset.jmgoAccordionInitialized === 'true') return;
    group.dataset.jmgoAccordionInitialized = 'true';

    const items = Array.from(group.querySelectorAll(':scope > [data-jmgo-spec-item]'));
    if (items.length && !items.some((item) => item.classList.contains('is-open'))) {
      setAccordionState(items[0], true);
    }

    items.forEach((item) => {
      const trigger = item.querySelector('[data-jmgo-accordion-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', () => {
        const wasOpen = item.classList.contains('is-open');
        items.forEach((other) => setAccordionState(other, false));
        if (!wasOpen) setAccordionState(item, true);
      });
    });
  };

  const centerSpecTab = (root, button) => {
    if (!root || !button || window.innerWidth > 749) return;
    const nav = button.closest('.jmgo-spec-tabs__nav');
    if (!nav) return;
    const target = button.offsetLeft - (nav.clientWidth - button.offsetWidth) / 2;
    nav.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  };

  const activateSpecTab = (root, key, focus = false) => {
    const buttons = Array.from(root.querySelectorAll('[data-jmgo-spec-tab-button]'));
    const panels = Array.from(root.querySelectorAll('[data-jmgo-spec-panel]'));
    let activeButton = null;

    buttons.forEach((button) => {
      const active = button.dataset.jmgoSpecTabButton === key;
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      button.tabIndex = active ? 0 : -1;
      if (active) activeButton = button;
      if (active && focus) button.focus({ preventScroll: true });
    });

    panels.forEach((panel) => {
      const active = panel.dataset.jmgoSpecPanel === key;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
      if (active) {
        const group = panel.querySelector('[data-jmgo-accordion-group]');
        if (group) {
          initAccordionGroup(group);
          const items = Array.from(group.querySelectorAll(':scope > [data-jmgo-spec-item]'));
          if (items.length && !items.some((item) => item.classList.contains('is-open'))) {
            setAccordionState(items[0], true);
          }
        }
      }
    });

    centerSpecTab(root, activeButton);
  };

  const setMobileSpecPanelState = (panel, open) => {
    if (!panel) return;
    panel.classList.toggle('is-mobile-open', open);
    const toggle = panel.querySelector('[data-jmgo-mobile-spec-tab-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      const label = toggle.querySelector('[data-jmgo-mobile-spec-tab-label]');
      if (label) label.textContent = open ? '詳細を閉じる' : '詳細を表示';
    }
  };

  const ensureFirstSpecItemOpen = (panel, reset = false) => {
    if (!panel) return;
    const group = panel.querySelector('[data-jmgo-accordion-group]');
    if (!group) return;
    initAccordionGroup(group);
    const items = Array.from(group.querySelectorAll(':scope > [data-jmgo-spec-item]'));
    if (!items.length) return;
    if (reset) {
      items.forEach((item, index) => setAccordionState(item, index === 0));
      return;
    }
    if (!items.some((item) => item.classList.contains('is-open'))) {
      setAccordionState(items[0], true);
    }
  };

  const syncSpecTabsResponsiveMode = (root) => {
    if (!root) return;
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const panels = Array.from(root.querySelectorAll('[data-jmgo-spec-panel]'));
    const buttons = Array.from(root.querySelectorAll('[data-jmgo-spec-tab-button]'));
    if (!panels.length) return;

    if (mobile) {
      // SPでは全タブ見出しを縦に表示し、選択したタブの中身だけを展開する。
      panels.forEach((panel) => {
        panel.hidden = false;
      });

      let openPanel = panels.find((panel) => panel.classList.contains('is-mobile-open'));
      if (!openPanel) {
        openPanel = panels[0];
        setMobileSpecPanelState(openPanel, true);
      }
      panels.forEach((panel) => {
        if (panel !== openPanel) setMobileSpecPanelState(panel, false);
      });
      ensureFirstSpecItemOpen(openPanel, true);
      return;
    }

    // PCへ戻った時は通常のタブUIに復帰。
    const selected = buttons.find((button) => button.getAttribute('aria-selected') === 'true') || buttons[0];
    if (selected) activateSpecTab(root, selected.dataset.jmgoSpecTabButton);
  };

  const initSpecTabs = (root, force = false) => {
    if (!root) return;
    if (root.dataset.jmgoSpecTabsInitialized === 'true' && !force) return;

    const buttons = Array.from(root.querySelectorAll('[data-jmgo-spec-tab-button]'));
    const panels = Array.from(root.querySelectorAll('[data-jmgo-spec-panel]'));
    if (!buttons.length || !panels.length) return;

    root.dataset.jmgoSpecTabsInitialized = 'true';

    const mobileToggles = Array.from(root.querySelectorAll('[data-jmgo-mobile-spec-tab-toggle]'));
    mobileToggles.forEach((toggle) => {
      if (toggle.dataset.jmgoMobileClickBound === 'true') return;
      toggle.dataset.jmgoMobileClickBound = 'true';
      toggle.addEventListener('click', () => {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        const panel = toggle.closest('[data-jmgo-spec-panel]');
        if (!panel) return;
        const willOpen = !panel.classList.contains('is-mobile-open');
        panels.forEach((other) => setMobileSpecPanelState(other, false));
        if (willOpen) {
          setMobileSpecPanelState(panel, true);
          ensureFirstSpecItemOpen(panel, true);
        }
      });
    });

    buttons.forEach((button, index) => {
      if (button.dataset.jmgoClickBound !== 'true') {
        button.dataset.jmgoClickBound = 'true';
        button.addEventListener('click', () => {
          activateSpecTab(root, button.dataset.jmgoSpecTabButton);
        });

        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const currentButtons = Array.from(root.querySelectorAll('[data-jmgo-spec-tab-button]'));
          const currentIndex = currentButtons.indexOf(button);
          let targetIndex = currentIndex;

          if (event.key === 'ArrowRight') targetIndex = (currentIndex + 1) % currentButtons.length;
          if (event.key === 'ArrowLeft') targetIndex = (currentIndex - 1 + currentButtons.length) % currentButtons.length;
          if (event.key === 'Home') targetIndex = 0;
          if (event.key === 'End') targetIndex = currentButtons.length - 1;

          const target = currentButtons[targetIndex];
          activateSpecTab(root, target.dataset.jmgoSpecTabButton, true);
        });
      }

      if (index === 0 && button.getAttribute('aria-selected') !== 'true') {
        button.setAttribute('aria-selected', 'true');
      }
    });

    panels.forEach((panel) => {
      const group = panel.querySelector('[data-jmgo-accordion-group]');
      if (group) initAccordionGroup(group);
    });

    const selected = buttons.find((button) => button.getAttribute('aria-selected') === 'true') || buttons[0];
    activateSpecTab(root, selected.dataset.jmgoSpecTabButton);
    syncSpecTabsResponsiveMode(root);

    if (root.dataset.jmgoResponsiveBound !== 'true') {
      root.dataset.jmgoResponsiveBound = 'true';
      let resizeTimer = null;
      window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => syncSpecTabsResponsiveMode(root), 100);
      }, { passive: true });
    }
  };

  const initDetailSpecs = (root) => {
    if (!root || root.dataset.jmgoDetailInitialized === 'true') return;
    root.dataset.jmgoDetailInitialized = 'true';
    root.dataset.jmgoDetailExpanded = 'false';
    const button = root.querySelector('[data-jmgo-detail-toggle]');
    const label = root.querySelector('[data-jmgo-detail-toggle-label]');
    if (!button) return;

    const apply = () => {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const threshold = mobile ? 4 : 6;
      const total = Number(root.dataset.jmgoDetailCount || 0);
      const expanded = root.dataset.jmgoDetailExpanded === 'true';
      const tables = root.querySelectorAll(mobile ? '.jmgo-detail-specs__table--mobile' : '.jmgo-detail-specs__table--desktop');

      root.querySelectorAll('[data-jmgo-detail-row]').forEach((row) => row.classList.remove('is-jmgo-collapsed'));
      tables.forEach((table) => {
        table.querySelectorAll('[data-jmgo-detail-row]').forEach((row) => {
          const index = Number(row.dataset.jmgoDetailIndex || 0);
          row.classList.toggle('is-jmgo-collapsed', !expanded && index > threshold);
        });
      });

      button.hidden = total <= threshold;
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (label) label.textContent = expanded ? '閉じる' : 'もっと見る';
    };

    button.addEventListener('click', () => {
      root.dataset.jmgoDetailExpanded = root.dataset.jmgoDetailExpanded === 'true' ? 'false' : 'true';
      apply();
    });
    window.addEventListener('resize', apply, { passive: true });
    apply();
  };

  const initArticleSlider = (root) => {
    if (!root || root.dataset.jmgoArticlesInitialized === 'true') return;
    root.dataset.jmgoArticlesInitialized = 'true';
    const slider = root.querySelector('[data-jmgo-articles-slider]');
    const prev = root.querySelector('[data-jmgo-articles-prev]');
    const next = root.querySelector('[data-jmgo-articles-next]');
    if (!slider || !prev || !next) return;
    const arrows = prev.closest('.jmgo-articles__arrows');

    const step = () => {
      const card = slider.querySelector('[data-jmgo-article-card]');
      if (!card) return slider.clientWidth * 0.85;
      const styles = getComputedStyle(slider);
      const gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const update = () => {
      const max = Math.max(0, slider.scrollWidth - slider.clientWidth - 2);
      prev.disabled = slider.scrollLeft <= 2;
      next.disabled = slider.scrollLeft >= max;
      if (arrows) arrows.hidden = max <= 2;
    };

    prev.addEventListener('click', () => slider.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => slider.scrollBy({ left: step(), behavior: 'smooth' }));
    slider.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    requestAnimationFrame(update);
  };

  const initShortVideos = (root) => {
    if (!root || root.dataset.jmgoShortVideosInitialized === 'true') return;
    root.dataset.jmgoShortVideosInitialized = 'true';

    const slider = root.querySelector('[data-jmgo-short-videos-slider]');
    const prev = root.querySelector('[data-jmgo-short-videos-prev]');
    const next = root.querySelector('[data-jmgo-short-videos-next]');
    const cards = Array.from(root.querySelectorAll('[data-jmgo-short-video-card]'));

    const pauseOthers = (activeVideo) => {
      root.querySelectorAll('[data-jmgo-short-video]').forEach((video) => {
        if (video !== activeVideo && !video.paused) video.pause();
      });
    };

    cards.forEach((card) => {
      const playButton = card.querySelector('[data-jmgo-short-video-play]');
      const video = card.querySelector('[data-jmgo-short-video]');
      const sourceTemplate = card.querySelector('[data-jmgo-short-video-sources]');
      if (!playButton || !video || !sourceTemplate) return;

      playButton.addEventListener('click', () => {
        if (video.dataset.jmgoLoaded !== 'true') {
          video.appendChild(sourceTemplate.content.cloneNode(true));
          video.dataset.jmgoLoaded = 'true';
          video.load();
        }
        pauseOthers(video);
        playButton.hidden = true;
        video.hidden = false;
        const promise = video.play();
        if (promise && typeof promise.catch === 'function') promise.catch(() => {});
      });

      video.addEventListener('play', () => pauseOthers(video));
    });

    if ('IntersectionObserver' in window) {
      const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!entry.isIntersecting && !video.paused) video.pause();
        });
      }, { threshold: 0.25 });
      root.querySelectorAll('[data-jmgo-short-video]').forEach((video) => videoObserver.observe(video));
    }

    if (!slider || !prev || !next) return;

    const step = () => {
      const card = slider.querySelector('[data-jmgo-short-video-card]');
      if (!card) return slider.clientWidth * 0.8;
      const styles = getComputedStyle(slider);
      const gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const updateArrows = () => {
      const max = Math.max(0, slider.scrollWidth - slider.clientWidth - 2);
      prev.disabled = slider.scrollLeft <= 2;
      next.disabled = slider.scrollLeft >= max;
      const arrows = prev.closest('.jmgo-short-videos__arrows');
      if (arrows) arrows.hidden = max <= 2;
    };

    prev.addEventListener('click', () => slider.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => slider.scrollBy({ left: step(), behavior: 'smooth' }));
    slider.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows, { passive: true });
    requestAnimationFrame(updateArrows);
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-jmgo-hero]').forEach(initHero);
    scope.querySelectorAll('[data-jmgo-spec-tabs-section]').forEach((section) => initSpecTabs(section));
    scope.querySelectorAll('[data-jmgo-detail-specs]').forEach(initDetailSpecs);
    scope.querySelectorAll('[data-jmgo-articles-section]').forEach(initArticleSlider);
    scope.querySelectorAll('[data-jmgo-short-videos-section]').forEach(initShortVideos);
  };

  if (!window.JMGOProductPage) {
    window.JMGOProductPage = { init, initSpecTabs, activateSpecTab };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => init(document));
    } else {
      init(document);
    }

    document.addEventListener('shopify:section:load', (event) => init(event.target));

    document.addEventListener('shopify:block:select', (event) => {
      const selectedBlock = event.target;
      const section = selectedBlock.closest('[data-jmgo-spec-tabs-section]');
      if (!section) return;

      initSpecTabs(section, true);

      const directTabButton = selectedBlock.matches('[data-jmgo-spec-tab-button]')
        ? selectedBlock
        : selectedBlock.querySelector('[data-jmgo-spec-tab-button]');

      if (directTabButton) {
        activateSpecTab(section, directTabButton.dataset.jmgoSpecTabButton);
        syncSpecTabsResponsiveMode(section);
        return;
      }

      const item = selectedBlock.matches('[data-jmgo-spec-item]')
        ? selectedBlock
        : selectedBlock.closest('[data-jmgo-spec-item]');

      if (item && item.dataset.jmgoOwnerTab) {
        activateSpecTab(section, item.dataset.jmgoOwnerTab);
        const group = item.closest('[data-jmgo-accordion-group]');
        if (group) {
          group.querySelectorAll(':scope > [data-jmgo-spec-item]').forEach((other) => setAccordionState(other, false));
        }
        setAccordionState(item, true);
        syncSpecTabsResponsiveMode(section);
      }
    });
  } else {
    window.JMGOProductPage.init = init;
    window.JMGOProductPage.initSpecTabs = initSpecTabs;
    window.JMGOProductPage.activateSpecTab = activateSpecTab;
    init(document);
  }
})();